import { useParams } from 'react-router-dom'
import Table from '../../components/Table'
import { useEffect, useState } from 'react'
import { customerService } from '../../services/customer.service'
import { VenueDto, VenueSpecialDto } from '@ivy-monorepo/shared'
import PropertyBar from '../../components/PropertyBar'
import TextInput from '../../components/ui/TextInput'

type EditableVenueSpecial = Omit<VenueSpecialDto, 'dayOfWeek'> & { dayOfWeek?: string | null }

const VenuePage = () => {
  const { publicId } = useParams<{ publicId: string }>()
  const [venue, setVenue] = useState<VenueDto | undefined>(undefined)
  const [specials, setSpecials] = useState<VenueSpecialDto[]>([])
  const [editSpecial, setEditSpecial] = useState<EditableVenueSpecial>({} as EditableVenueSpecial)
  const [isPropertyBarVisible, setIsPropertyBarVisible] = useState(false)

  useEffect(() => {
    const fetchVenue = async () => {
      if (!publicId) return
      const venue = await customerService.getVenue(publicId)
      setVenue(venue)
      setSpecials(venue.specials ?? [])
    }
    fetchVenue()
  }, [publicId])

  if (!venue) {
    return <div></div>
  }

  const handleRowClick = (special: VenueSpecialDto) => {
    setEditSpecial({
      ...special,
      dayOfWeek: special.dayOfWeek != null ? special.dayOfWeek.toString() : '',
    })
    setIsPropertyBarVisible(true)
  }

  const handleAddSpecial = () => {
    setEditSpecial({
      publicId: '',
      name: '',
      content: '',
      dayOfWeek: '',
      startDate: '',
      endDate: '',
    } as unknown as EditableVenueSpecial)
    setIsPropertyBarVisible(true)
  }

  return (
    <div>
      <div className="venue-container">
        <div className="header-row">
          <h2>{venue.name} specials</h2>
          <button className="styled-button" onClick={handleAddSpecial}>
            Add special
          </button>
        </div>
        <Table
          id="venue-specials"
          name="Specials"
          keyField="publicId"
          initialSortColumn="name"
          isPropertyBarVisible={isPropertyBarVisible}
          columns={[
            { label: 'Name', accessor: 'name', type: 'string' },
            { label: 'Content', accessor: 'content', type: 'string', className: 'special-content' },
            { label: 'Day of week', accessor: 'dayOfWeek', type: 'dayOfWeek' },
            { label: 'Start', accessor: 'startDate', type: 'dateTime' },
            { label: 'End', accessor: 'endDate', type: 'dateTime' },
          ]}
          sourceData={specials}
          filterTerms=""
          onRowClick={handleRowClick}
        />
      </div>
      {isPropertyBarVisible && (
        <PropertyBar
          entityID={editSpecial.publicId}
          isVisible={isPropertyBarVisible}
          onSave={async () => {
            const normalizedDayOfWeek =
              editSpecial.dayOfWeek !== undefined && editSpecial.dayOfWeek !== null && editSpecial.dayOfWeek !== '' ? parseInt(editSpecial.dayOfWeek, 10) : null
            const payload = {
              ...editSpecial,
              dayOfWeek: normalizedDayOfWeek,
            }

            if (editSpecial.publicId) {
              await customerService.updateVenueSpecial(publicId!, editSpecial.publicId, payload)
              setSpecials(prev =>
                prev.map(s => {
                  if (s.publicId !== editSpecial.publicId) {
                    return s
                  }

                  const updatedSpecial = {
                    ...s,
                    ...payload,
                  } as VenueSpecialDto

                  return updatedSpecial
                })
              )
            } else {
              await customerService.createVenueSpecial(publicId!, payload)
              const venueUpdated = await customerService.getVenue(publicId!)
              setSpecials(venueUpdated.specials ?? [])
            }
          }}
          onCancel={() => {
            setTimeout(() => setIsPropertyBarVisible(false), 300)
          }}
        >
          <>
            <div className="caption">{editSpecial.publicId ? 'Edit Special' : 'New Special'}</div>
            <TextInput
              label="Name"
              name="name"
              type="text"
              required={true}
              value={editSpecial.name || ''}
              onChange={(val: string) => setEditSpecial(prev => ({ ...prev, name: val }))}
            />
            <TextInput
              label="Content"
              name="content"
              type="textArea"
              required={true}
              value={editSpecial.content || ''}
              onChange={(val: string) => setEditSpecial(prev => ({ ...prev, content: val }))}
            />
            <TextInput
              label="Day of week"
              name="dayOfWeek"
              type="dayOfWeek"
              required={false}
              value={editSpecial.dayOfWeek ?? ''}
              onChange={(val: string) => setEditSpecial(prev => ({ ...prev, dayOfWeek: val ?? '' }))}
            />
            <TextInput
              label="Start date"
              name="startDate"
              type="date"
              required={false}
              value={editSpecial.startDate || ''}
              onChange={(val: string) => setEditSpecial(prev => ({ ...prev, startDate: val }))}
            />
            <TextInput
              label="End date"
              name="endDate"
              type="date"
              required={false}
              value={editSpecial.endDate || ''}
              onChange={(val: string) => setEditSpecial(prev => ({ ...prev, endDate: val }))}
            />
          </>
        </PropertyBar>
      )}
    </div>
  )
}

export default VenuePage
